/**
 * What reaches a service, read once for every part of its page.
 *
 * Round 10 C4. The reach sentence landed in round 9 and the rest of the
 * page kept answering from its own queries: the sentence said eight
 * findings reach this service through its configuration items, and
 * eight lines below "No open findings on this entity"; it named three
 * business services, and three lines below "IBSES n/a". The new
 * sentence and the old tiles were written for different behaviours and
 * both shipped.
 *
 * One hook, one query key, so the parts of one page cannot disagree
 * about one service.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

export interface ServiceReach {
  findings_direct: number;
  findings_via_configuration_items: number;
  ibses: { id: string; name: string }[];
  /** Round 20 P22 -- of those via its configuration items, the critical
   *  and high ones (the cascade's cut). */
  findings_via_configuration_items_serious?: number;
  /** Round 20 P20 -- the reach by its cut. */
  ibses_direct_admitted?: number;
  ibses_reached_admitted?: number;
  dependent_services?: number;
}

export function serviceReachKey(serviceId: string) {
  return ["service-reach", serviceId] as const;
}

export function useServiceReach(
  serviceId: string | undefined,
): UseQueryResult<ServiceReach, ApiError> {
  return useQuery<ServiceReach, ApiError>({
    queryKey: serviceReachKey(serviceId ?? ""),
    queryFn: () => api.get<ServiceReach>(`/v1/services/${serviceId}/reach`),
    enabled: Boolean(serviceId),
    retry: false,
  });
}
